import { Injectable, inject } from '@angular/core';
import {
  CompareSettings,
  Settings as _Settings,
  Parameters,
  SearchSettings,
  ProtocolSettings,
} from '../utils/interfaces/settings.interface';
import { BackendResponse, StoreV2 } from '@schenck/rotec-components';
import { map, skip, skipWhile } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { SettingsApi } from 'src/api/settings.api';
import {
  compareSettingsDefault,
  defaultParameters,
  protocolSettingsDefault,
  searchSettingsDefaults,
  unitSettingsDefault,
} from 'src/utils/constants/settings.const';

export interface Settings extends _Settings {
  initialized: boolean;
}

@Injectable()
export class SettingsService extends StoreV2<Settings> {
  private readonly settingsApi = inject(SettingsApi);

  /**
   * Same as state$, but waits with emitting values until settings initialized.
   */
  settings$ = this.state$.pipe(skipWhile((settings) => !settings.initialized));
  loading$ = this.state$.pipe(map((settings) => !settings.initialized));

  constructor() {
    super(
      {
        showAverage: compareSettingsDefault.showAverage,
        parameters: defaultParameters,
        searchSettings: searchSettingsDefaults,
        initialized: false,
        unitSettings: unitSettingsDefault,
        protocolSettings: protocolSettingsDefault,
      },
      'SettingsService'
    );
    this.loadSettings();
  }

  /**
   * Sets compare units for measurements and persists that in the backend.
   */
  setCompareSettings(compareSettings: CompareSettings) {
    this.patchState('set compare settings', compareSettings);
    this.settingsApi.setCompareSettings(compareSettings);
  }

  /**
   * Sets parameters for given machine type and persists that in the backend.
   */
  setParameters(parameters: Parameters) {
    this.setState('set parameters', (state) => ({
      ...state,
      parameters,
    }));
    this.settingsApi.setParameters(parameters);
  }

  /**
   * Updates search settings and persists that in the backend.
   */
  updateSearchSettings(searchSettings: Partial<SearchSettings>) {
    this.setState('update search settings', (state) => ({
      ...state,
      searchSettings: { ...state.searchSettings, ...searchSettings },
    }));
    this.settingsApi.setSearchSettings(this.state.searchSettings);
  }

  /**
   * Updates search settings and persists that in the backend.
   */
  updateProtocolSettings$(
    protocolSettings: Partial<ProtocolSettings>
  ): Observable<BackendResponse.Response<ProtocolSettings>> {
    const newProtocolSettings = { ...this.state.protocolSettings, ...protocolSettings };

    return this.settingsApi.setProtocolSettings$(newProtocolSettings).pipe(
      map((response) => {
        if (BackendResponse.isSuccess(response)) {
          this.setState('update protocol settings', (state) => ({
            ...state,
            protocolSettings: { ...state.protocolSettings, ...response.result },
          }));
        }
        return response;
      })
    );
  }

  private loadSettings() {
    this.settingsApi
      .load$()
      .subscribe((settings) => this.setState('init settings', { ...settings, initialized: true }));

    // above observable is completed after first load, but unit settings
    // need to be updated when values are updated
    // more information DIG-17339
    this.settingsApi
      .loadUnitSettings$()
      .pipe(skip(1))
      .subscribe((unitSettings) => this.patchState('patch settings', { unitSettings }));
  }
}
